import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IntlShape } from 'react-intl';
import { EmailTemplateType, IEmailTemplateFields, IEmailTemplatesErrorConfig } from '../../../../../../../api/email-configuration.dao';
import { LONG_RICH_TEXT_LENGTH, SHORT_PLAIN_TEXT_LENGTH } from '../../../../../../../constants/common.constants';
import { calcByteCodeSize } from '../../../../../../../infrastructure/utils/common.utils';
import UploadImageS3VM from '../../../../shared/upload-image/upload-image-to-s3.viewmodel';
import { IAlertsService } from '../../../../../../../infrastructure/alerts';
import { IErrorParsing } from '../../../../../../../infrastructure/error-parsing';
import { transpileClassesToInlineStyles, transpileInlineStylesToClasses } from '../../../../../../global/rich-text-editor/utils/transpiler';

const defaultEmailTemplateFields: IEmailTemplateFields = {
    body: '',
    subject: '',
    location: '',
    logo: null,
    name: '',
    type: '',
    subtype: '',
    enabled: true,
    autoSubscribed: false
};

class CreateEmailTemplateViewModel {
    @mobx.observable
    public isSaving: boolean;
    @mobx.observable
    private startingSubTypeChanged = false;
    @mobx.observable
    public startingTypeChanged = false;
    @mobx.observable
    public emailTemplatePlaceholder: IEmailTemplateFields;
    @mobx.observable
    public emailTemplatesErrorHandler: IEmailTemplatesErrorConfig = {};
    @mobx.observable
    public uploadNewEmailLogoImageS3VM: UploadImageS3VM = null;


    constructor(
        public emailTemplateFormVM,
        public readonly intl: IntlShape,
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
    ) {
        this.emailTemplatePlaceholder = { ...defaultEmailTemplateFields };
        this.uploadNewEmailLogoImageS3VM = new UploadImageS3VM(
            this.emailTemplateFormVM.getAccountId,
            this.emailTemplateFormVM.getEventId,
            'logo',
            'email-configuration',
            null,
            null,
            true
        );
        this.uploadNewEmailLogoImageS3VM.addImage(
            this.emailTemplateFormVM.emailTemplates[0].logo?.assetUrl,
            this.emailTemplateFormVM.emailTemplates[0].logo?.title,
            this.emailTemplateFormVM.emailTemplates[0].logo?.objectKey
        );
    }

    @mobx.action
    public setSelectedStartingTemplate = (startingTemplateType: string): void => {
        this.startingTypeChanged = true;
        const parentTemplate = this.emailTemplateFormVM.initialEmailTemplates.find(t => t.type === startingTemplateType);
        this.emailTemplatePlaceholder.type = startingTemplateType;
        this.setEmailSubject(parentTemplate?.subject || '');
        this.setEmailBody(parentTemplate?.body || '');
        if (this.isCalendarInvitationTemplateType) this.setTemplateEmailLocation(parentTemplate?.location || '');
    };

    public get currentTemplateType(): string {
        return this.emailTemplatePlaceholder.type;
    }

    public get isCalendarInvitationTemplateType(): boolean {
        return this.currentTemplateType === EmailTemplateType.REGISTRATION_CALENDAR_INVITATION
            || this.currentTemplateType === EmailTemplateType.MEETING_CALENDAR_INVITATION
            || this.currentTemplateType === EmailTemplateType.BOOKABLE_MEETING_CALENDAR_INVITATION
            || this.currentTemplateType === EmailTemplateType.PRESENTER_BOOKABLE_MEETING_CALENDAR_INVITATION;
    }

    @mobx.action
    public setSelectedSubType = (startingSubType: string): void => {
        this.emailTemplatePlaceholder.subtype = startingSubType;
        this.startingSubTypeChanged = true;
    };

    public get currentTemplateSubType(): string {
        return this.emailTemplatePlaceholder.subtype;
    }

    @mobx.action
    public setEmailSubject(value: string): void {
        this.emailTemplatePlaceholder.subject = value;
        this.setEmailSubjectError(value);
    }

    @mobx.action
    public setEmailSubjectError(value: string): void {
        if (value.trim().length > SHORT_PLAIN_TEXT_LENGTH) {
            this.emailTemplatesErrorHandler = {
                ...this.emailTemplatesErrorHandler,
                emailSubjectError: this.intl.formatMessage({
                    description: 'Warning message',
                    defaultMessage: 'Email subject is too long',
                    id: 'oe.common.emailSubjectTooLong',
                })
            };
        } else if (!value.trim().length) {
            this.emailTemplatesErrorHandler = {
                ...this.emailTemplatesErrorHandler,
                emailSubjectError: this.intl.formatMessage({
                    description: 'Warning message',
                    defaultMessage: 'Email subject is required',
                    id: 'oe.common.emailSubjectRequired',
                })
            };
        } else {
            delete this.emailTemplatesErrorHandler?.emailSubjectError;
        }
    }

    public get getSelectedTemplateSubjectError(): string {
        return this.emailTemplatesErrorHandler.emailSubjectError;
    }

    @mobx.action
    public setEmailBody(value: string): void {
        this.emailTemplatePlaceholder.body = value;
        const error = this.getTemplateEmailBodyError();
        if (error) {
            this.emailTemplatesErrorHandler = {
                ...this.emailTemplatesErrorHandler,
                emailBodyError: error
            };
        } else {
            delete this.emailTemplatesErrorHandler?.emailBodyError;
        }
    }

    private getTemplateEmailBodyError(): string | null {
        return calcByteCodeSize(this.emailTemplatePlaceholder.body) > LONG_RICH_TEXT_LENGTH ?
            this.intl.formatMessage({
                description: 'Warning message',
                defaultMessage: 'Email body is too long',
                id: 'oe.common.emailBodyTooLong',
            }) : !this.emailTemplatePlaceholder.body.length && !this.isCalendarInvitationTemplateType ?
            this.intl.formatMessage({
                description: 'Warning message',
                defaultMessage: 'Email body is required',
                id: 'oe.common.emailBodyRequired',
            }) :
            null
        ;
    }

    @mobx.action
    public setTemplateEmailLocation(value: string): void {
        this.emailTemplatePlaceholder.location = value;
        this.setTemplateEmailLocationError(value);
    }

    @mobx.action
    private setTemplateEmailLocationError(value: string): void {
        if (value.trim().length > SHORT_PLAIN_TEXT_LENGTH) {
            this.emailTemplatesErrorHandler = {
                ...this.emailTemplatesErrorHandler,
                emailLocationError: this.intl.formatMessage({
                    description: 'Warning message',
                    defaultMessage: 'Location is too long',
                    id: 'oe.common.emailSubjectTooLong',
                })
            };
        } else {
            delete this.emailTemplatesErrorHandler?.emailLocationError;
        }
    }

    public get templateEmailLocationError(): string | void {
        return this.emailTemplatesErrorHandler.emailLocationError;
    }

    public get isFormValid(): boolean {
        const errorInFields = Object.keys(this.emailTemplatesErrorHandler).length;
        return !errorInFields && this.isTemplateCombinationUnique;
    }

    public get isTemplateCombinationUnique(): boolean | undefined {
        if (!this.startingSubTypeChanged
            || !this.emailTemplatePlaceholder.subtype
            || !this.emailTemplatePlaceholder.type
        ) return;

        const templatesWithSelectedSubtype = this.emailTemplateFormVM.initialEmailTemplates
            .filter(t => t.subtype === this.emailTemplatePlaceholder.subtype);
        if (templatesWithSelectedSubtype.some(t => t.type === this.emailTemplatePlaceholder.type)) return false;

        return true;
    }

    @mobx.action.bound
    public async onCreateEmailTemplate(): Promise<boolean> {
        this.isSaving = true;
        try {
            const templateLogo = this.uploadNewEmailLogoImageS3VM.getImageToApi();
            const emailTemplatesForUpdate = [
                ...this.emailTemplateFormVM.initialEmailTemplates,
                {
                    ...this.emailTemplatePlaceholder,
                    body: transpileClassesToInlineStyles(this.emailTemplatePlaceholder.body),
                    logo: this.emailTemplateFormVM.initialEmailTemplates[0].logo
                }
            ].map(t => ({
                ...t,
                ...(!this.uploadNewEmailLogoImageS3VM.getImageChanged ? {} : {...templateLogo}),
            }));
            const response = await this.emailTemplateFormVM.getEmailConfigurationDAO.updateTemplatesConfiguration({
                accountId: this.emailTemplateFormVM.getAccountId,
                eventId: this.emailTemplateFormVM.getEventId,
                configurations: emailTemplatesForUpdate
            });

            response.configurations.forEach(config => {
                const transpiledBody = transpileInlineStylesToClasses(config.body);
                config.body = transpiledBody;
            });

            mobx.runInAction(() => {
                const createdEmail = response.configurations.find(
                    t => t.type === this.emailTemplatePlaceholder.type
                        && t.subtype === this.emailTemplatePlaceholder.subtype
                );

                /* manually set logo and configurationId to keep in sync templates on UI with data from API */
                this.emailTemplateFormVM.emailTemplates.forEach(vmTemplate => {
                    const responseTemplate = response.configurations.find(e => e.name === vmTemplate.name);
                    if (responseTemplate) {
                        vmTemplate.logo = responseTemplate?.logo;
                        vmTemplate.configurationId = responseTemplate.configurationId;
                    }
                });

                this.emailTemplateFormVM.emailTemplates.push(createdEmail);
                this.emailTemplateFormVM.initialEmailTemplates = [...response.configurations];
                this.emailTemplateFormVM.setEmailTemplatesNames(response.configurations);
                this.emailTemplateFormVM.uploadEmailLogoImageS3VM.addImage(
                     response.configurations[0].logo?.assetUrl,
                     response.configurations[0].logo?.title,
                     response.configurations[0].logo?.objectKey
                );
                this.emailTemplateFormVM.uploadEmailLogoImageS3VM.setImageChanged(false);
                this.emailTemplateFormVM.addTemplateForTracking(createdEmail.name);
            });

            this.emailTemplateFormVM.setCurrentTemplate(this.emailTemplateFormVM.currentTemplate);
            return true;
        } catch (error) {
            this.alertsService.alert({
                message: this.errorParsingService.extractMessage(error),
            });
            return false;
        } finally {
            mobx.runInAction(() => {
                this.isSaving = false;
            });
        }
    }

    public get selectedStartingTemplateName(): string {
        return this.emailTemplateFormVM.availableTypes
            .find(t => t.type === this.emailTemplatePlaceholder.type)?.name;
    }
}

export default CreateEmailTemplateViewModel;
